import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { TaskScheduleTemplateService } from '../service/task.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CodemirrorComponent } from 'ng2-codemirror';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { environment } from 'src/environments/environment';
import { VariableBrowser } from '../variable/variable.browser';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgForm } from '@angular/forms';

@Component({
	selector: 'app-task-schedule-detail',
	templateUrl: './detail.component.html',
	styleUrls: ['./detail.component.scss']
})
export class TaskScheduleDetailComponent implements OnInit, OnDestroy {

	apiMethods = environment.content.apiMethods;
	accessRoles = environment.content.accessRoles;
	activeStates = environment.content.activeStates;
	apiNames = environment.content.apiNames;

	id: string;
	task = {
		state: 'Active',
		accessRoles: [ 'Root' ]
	} as any;
	routeSubscription: Subscription;

	config = {
		lineNumbers: true,
		mode: 'application/json',
		json: true
	};

	public invalidBody: string;
	@ViewChild('editor', {static: true}) editor: CodemirrorComponent;
	
	@ViewChild(MatAccordion, { static: true }) accordion: MatAccordion;
	@ViewChild('templateForm', {static: false}) templateForm: NgForm;
	
	constructor(
		private dialog: MatDialog,
		private router: Router,
		private route: ActivatedRoute,
		private taskService: TaskScheduleTemplateService,
		private translateService: TranslateService
	) {
		this.routeSubscription = this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				this.id = this.route.snapshot.paramMap.get('id');

				if (this.id) {
					this.taskService.get(this.id).subscribe((task: any) => {
						if (task.apiBody) {
							task.apiBody = JSON.stringify(task.apiBody, null, 2);
						}
						this.task = task;
					});
				} else {
					this.task = {
						apiName: this.apiNames[0].id,
						apiMethod: 'POST',
						state: 'Active',
						accessRoles: [ 'Root' ],
						variables: []
					};
				}
				const that = this;
				setTimeout(() => {
					that.editor.instance.refresh();
				}, 300);
			}
		});
	}

	ngOnInit() {
	}

	ngOnDestroy() {
		this.routeSubscription.unsubscribe();
	}

	isJson(jsonString: string) {
		if (!jsonString || jsonString.length === 0) {
			return true;
		} else {
			try {
				var json = JSON.parse(jsonString);
				return (typeof json === 'object');
			} catch (error) {
				return false;
			}	
		}
	}

	validateBody() {
		if (this.task.apiBody) {
			if (this.task.apiBody.length > 65535) {
				this.invalidBody = "body-length-invalid";
				return;
			} else if (!this.isJson(this.task.apiBody)) {
				this.invalidBody = "body-json-invalid";
				return;
			} else {
				this.invalidBody = null;
			}
		}
	}

	onBodyChange() {
		this.validateBody();
	}

	doSave() {
		this.translateService.get([
			'common.invalid.alert',
			'setting.task.schedule.detail.confirm.save',
			'setting.task.schedule.detail.alert.missing-name',
			'setting.task.schedule.detail.alert.missing-api-url',
			'setting.task.schedule.detail.alert.saved',
			'setting.task.schedule.detail.alert.save-failed'
		]).subscribe((messages: any) => {
			this.validateBody();

			if (!this.templateForm.valid || this.invalidBody) {
				alert(messages['common.invalid.alert']);
				if (this.templateForm.controls) {
					for (const control in this.templateForm.controls) {
						this.templateForm.controls[control].markAsTouched();
					}
				}
				return;
			} else {
				if (!this.task.name || this.task.name.trim() === '') {
					alert(messages['setting.task.schedule.detail.alert.missing-name']);
				} else if (!this.task.name || this.task.name.trim() === '') {
					alert(messages['setting.task.schedule.detail.alert.missing-api-url']);
				} else {
					if (confirm(messages['setting.task.schedule.detail.confirm.save'])) {
						let callOrder = 0;
						this.task.variables.forEach((variable: any) => {
							variable.callOrder = callOrder ++;
						});
						this.taskService.save(this.task, this.id).subscribe((taskTemplate: any) => {
							alert(messages['setting.task.schedule.detail.alert.saved']);
							if (!this.id) {
								this.router.navigate([ '/main', { outlets: { content: 'setting/task/schedule/detail/' + taskTemplate.id + '/' }}]);
							}
						}, () => {
							alert(messages['setting.task.schedule.detail.alert.save-failed']);
						})
					}  
				}	
			}
		});
	}

	doDelete() {
		this.translateService.get([
			'setting.task.schedule.detail.confirm.delete',
			'setting.task.schedule.detail.alert.deleted',
			'setting.task.schedule.detail.alert.delete-failed'
		]).subscribe((messages: any) => {
			if (confirm(messages['setting.task.schedule.detail.confirm.delete'])) {
				this.taskService.delete(this.id).subscribe((taskTemplate: any) => {
					alert(messages['setting.task.schedule.detail.alert.deleted']);
					this.router.navigate([ '/main', { outlets: { content: 'setting/task/schedule/list' }}]);
				}, () => {
					alert(messages['setting.task.schedule.detail.alert.delete-failed']);
				})
			}
		});
	}

	doAddVariable() {
		const variableSelect = new VariableBrowser(this.dialog);
		
		variableSelect.browse().subscribe((result: any) => {
			if (result.result === true) {
				const variable = result.data[0].getData();

				this.task.variables.push({
					variable: variable,
					name: variable.name,
					callOrder: this.task.variables.length,
					mandatory: true
				});
			}
		});
	}

	doDeleteVariable(variable: any) {
		this.translateService.get('setting.task.schedule.detail.confirm.delete-variable').subscribe((message: string) => {
			if (confirm(message)) {
				const index = this.task.variables.indexOf(variable);

				if (index !== -1) {
					this.task.variables.splice(index, 1);
				}
			}
		});
	}

	onDropVariable($event: CdkDragDrop<any[]>) {
		if ($event.previousContainer === $event.container) {
			moveItemInArray($event.container.data, $event.previousIndex, $event.currentIndex);
		}
	}

	goManageVariables() {
		this.router.navigate([ '/main', { outlets: { content: 'setting/task/schedule/variable/list' }}]);
	}

	goBack() {
		this.router.navigate([ '/main', { outlets: { content: 'setting/task/schedule/list' }}]);
	}

}
